import { t } from "@hakili/i18n";
import { Button, Card, Field } from "@hakili/ui";
import { useEffect, useState } from "react";

/**
 * `P0-UIK-029`, `00-SPINE` 6.3: **the invitation acceptance screen, built once.**
 *
 * ## Why it lives in this package, which says it holds no screens
 *
 * `DECISION ADDED A35` gave `app-shell` the composition plumbing and said in as
 * many words that screens and chrome stay in the apps , six apps sharing chrome
 * stop being six products. **The architect's ruling of 5 September widens that
 * for this one screen, and the widening is narrow and reasoned**: three apps
 * writing a password form would each get the API's password rules subtly
 * different, and the one that got them wrong would look identical to the two
 * that did not. Nothing else moves here, and A35 stands for everything else.
 *
 * ## The token is the authorisation
 *
 * This is a PUBLIC route in `routing.ts`'s sense , 6.3.6's rule for
 * `/depot/:token`, and the reason `AuthGate` has `isPublicRoute` at all. The
 * holder has no session yet; sending them to a login would be sending them to a
 * door whose password this very screen exists to set.
 *
 * ## Three shapes of answer, and the API owns the words
 *
 * `GET /invitations/:token` answers `200`, or `410` for revoked, accepted and
 * expired ALIKE, or `404` for a token that never existed. **The three spent
 * states differ only in the sentence**, so this screen renders the API's
 * sentence verbatim and never maps the code: a screen that mapped `gone` to one
 * message would tell an expired holder that they had already accepted.
 *
 * ## What it discloses
 *
 * The holder's own name, their own address, and the organisation offering the
 * seat , exactly what the route returns and exactly what the email already told
 * them. **Who invited them is deliberately absent** from the route's answer, and
 * this screen invents no place to show it.
 */

/** What `GET /api/v1/invitations/:token` answers, and the whole of what may be shown. */
export interface InvitationPreview {
  readonly email: string;
  readonly fullName: string;
  readonly role: string;
  readonly organisationName: string;
  readonly expiresAt: string;
}

export interface InvitationScreenProps {
  /** The last segment of the link, decoded by the router. */
  readonly token: string;
  readonly origin: string;
  /** Passed in by a spec so the screen can be proved without a network. */
  readonly fetchImpl?: typeof globalThis.fetch;
  /**
   * The accept door OPENS A SESSION and returns its access token. The app
   * adopts it and goes to its own landing , which is why this is a callback and
   * not a redirect written here: `app-shell` does not know where an app lands.
   */
  readonly onAccepted: (accessToken: string) => void;
}

type Etat =
  | { readonly phase: "lecture" }
  | { readonly phase: "prete"; readonly invitation: InvitationPreview }
  | { readonly phase: "refusee"; readonly message: string };

/**
 * The API's own sentence, or a stated fallback.
 *
 * **Never a sentence of this screen's own when the API supplied one.** The
 * refusals here are the only place a holder learns whether to ask for a new
 * invitation or to simply sign in, and that distinction lives in the API's
 * text.
 */
const messageOf = (body: unknown, defaut: string): string => {
  const message = (body as { error?: { message?: unknown } } | null)?.error?.message;
  return typeof message === "string" && message !== "" ? message : defaut;
};

export function InvitationScreen({
  token,
  origin,
  fetchImpl,
  onAccepted,
}: InvitationScreenProps): React.JSX.Element {
  const [etat, setEtat] = useState<Etat>({ phase: "lecture" });
  const [motDePasse, setMotDePasse] = useState("");
  const [erreur, setErreur] = useState<string | null>(null);
  const [enCours, setEnCours] = useState(false);

  useEffect(() => {
    const appel = fetchImpl ?? globalThis.fetch;
    // An `AbortController` and not a boolean flag: it CANCELS the request as
    // well as ignoring its answer, and a boolean would only do the second.
    const abandon = new AbortController();
    void (async () => {
      try {
        const response = await appel(`${origin}/api/v1/invitations/${encodeURIComponent(token)}`, {
          headers: { accept: "application/json" },
          signal: abandon.signal,
        });
        const body: unknown = await response.json().catch(() => null);
        if (!response.ok) {
          setEtat({ phase: "refusee", message: messageOf(body, t("common.invitation.refusParDefaut")) });
          return;
        }
        setEtat({ phase: "prete", invitation: body as InvitationPreview });
      } catch {
        // Includes the abort itself, and that is the right shape: a screen that
        // has been left has nothing to say, and `setEtat` on an unmounted one is
        // the warning nobody reads and the leak nobody finds.
        if (!abandon.signal.aborted) setEtat({ phase: "refusee", message: t("common.error.reseau") });
      }
    })();
    return () => {
      abandon.abort();
    };
  }, [token, origin, fetchImpl]);

  const accepter = async (): Promise<void> => {
    if (enCours || etat.phase !== "prete") return;
    setErreur(null);
    // The API is the arbiter of what a password may be , `assertAcceptablePassword`
    // refuses, among other things, a password containing the address's local
    // part. A second rule written here would be a second rule to keep in step,
    // so the only check made locally is that something was typed at all.
    if (motDePasse === "") {
      setErreur(t("common.invitation.motDePasseRequis"));
      return;
    }
    setEnCours(true);
    try {
      const appel = fetchImpl ?? globalThis.fetch;
      const response = await appel(`${origin}/api/v1/invitations/${encodeURIComponent(token)}/accept`, {
        method: "POST",
        headers: { accept: "application/json", "content-type": "application/json" },
        // **No `credentials`, and no bearer**, which `P0-UIK-024` books as a
        // rule: the token in the path is the whole of this caller's
        // authorisation, and the route is `@Public()`, so a cookie sent here
        // could only ever authorise SOMEBODY ELSE. It briefly said
        // `credentials: "include"` , written for the refresh cookie the door
        // sets , and on staging that carried the founder's console session to
        // a screen for a dealership they had never joined.
        body: JSON.stringify({ password: motDePasse }),
      });
      const body: unknown = await response.json().catch(() => null);
      setEnCours(false);
      if (!response.ok) {
        setErreur(messageOf(body, t("common.invitation.refusParDefaut")));
        return;
      }
      const accessToken = (body as { accessToken?: unknown } | null)?.accessToken;
      if (typeof accessToken !== "string" || accessToken === "") {
        // The door answered success and handed over no session. Saying so is
        // honest; sending the holder to a login they now have a password for is
        // the fallback, and it belongs to the app rather than to this screen.
        setErreur(t("common.invitation.refusParDefaut"));
        return;
      }
      onAccepted(accessToken);
    } catch {
      setEnCours(false);
      setErreur(t("common.error.reseau"));
    }
  };

  if (etat.phase === "lecture") {
    return (
      <p className="p-6 text-sm text-gray-500" data-testid="invitation-lecture">
        {t("common.state.chargement")}
      </p>
    );
  }

  if (etat.phase === "refusee") {
    return (
      <Card>
        <h1 className="text-lg font-medium text-gray-900">{t("common.invitation.titre")}</h1>
        <p role="alert" className="mt-3 text-sm text-red-600" data-testid="invitation-refus">
          {etat.message}
        </p>
      </Card>
    );
  }

  return (
    <Card>
      {/* **Two nodes, not one glued string.** It read « Votre invitation ,
          Sikasso Motors » , a title joined to a name by a separator nobody
          chose, which in French reads as a punctuation error. The record names
          the organisation on its own line, as the console's fiche does, and
          then no separator has to be chosen at all. */}
      <p className="text-sm text-gray-500">{t("common.invitation.titre")}</p>
      <h1 className="text-lg font-medium text-gray-900" data-testid="invitation-organisation">
        {etat.invitation.organisationName}
      </h1>
      {/* `P0-UIK-030`. **One node, so the comma is written and not left to
          JSX.** `{a} , {b}` renders three children and a literal
          `" , "` between them , a space before the comma, which French
          typography does not have and which no other line in this product
          prints. Composed here instead, so the separator is a decision rather
          than whitespace in a template. */}
      <p className="mb-4 mt-1 text-sm text-gray-500" data-testid="invitation-personne">
        {`${etat.invitation.fullName}, ${etat.invitation.email}`}
      </p>

      <Field label={t("common.invitation.motDePasse")} htmlFor="invitation-mot-de-passe">
        <input
          id="invitation-mot-de-passe"
          type="password"
          autoComplete="new-password"
          value={motDePasse}
          onChange={(event) => {
            setMotDePasse(event.target.value);
          }}
          className="w-full rounded-xl border border-gray-200 px-3 py-2 text-sm"
          data-testid="invitation-mot-de-passe"
        />
      </Field>

      <div className="mt-4" data-testid="invitation-accepter">
        <Button
          onClick={() => {
            void accepter();
          }}
        >
          {t("common.invitation.accepter")}
        </Button>
      </div>

      {erreur === null ? null : (
        <p role="alert" className="mt-3 text-sm text-red-600" data-testid="invitation-erreur">
          {erreur}
        </p>
      )}
    </Card>
  );
}
